import React from "react"

import { RegexConfig } from "@/metrics/registry/configuration"

import { logger } from "@/lib/logger"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { BinaryMetricRecord } from "../base/BinaryMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"

const log = logger("TranscriptRegexMatchMetric")

function buildMatchSummary(args: {
  matchFound: boolean
  totalMatches: number
  pattern?: string
  role?: string
  matchMode?: string
}): string | null {
  const { matchFound, totalMatches, pattern, role, matchMode } = args
  if (!pattern) {
    return null
  }
  const roleLabel = role ? ` in ${role} messages` : ""
  const modeIsAbsence = matchMode === "absence"
  if (matchFound) {
    return `Pattern ${JSON.stringify(pattern)} matched ${totalMatches} message${
      totalMatches === 1 ? "" : "s"
    }${roleLabel}.`
  }
  return modeIsAbsence
    ? `Pattern ${JSON.stringify(pattern)} was absent${roleLabel}.`
    : `Pattern ${JSON.stringify(pattern)} did not match any message${roleLabel}.`
}

interface TranscriptRegexMatchExplanation {
  match_found?: boolean
  regex_pattern?: string
  role?: string
  match_mode?: string
  position?: string
  case_insensitive?: boolean
  matching_message_indices?: number[]
}

export class TranscriptRegexMatchMetricRecord extends BinaryMetricRecord {
  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(metricOutput: {
    result_json?: Record<string, unknown> | null
  }): boolean {
    const resultJson = metricOutput.result_json
    if (!resultJson?.regex_pattern) {
      return true
    }
    return (
      resultJson.regex_pattern === (this.metric_metadata?.regex_pattern || "")
    )
  }

  getVersionDiff(metricOutput: {
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    const diffs: Array<{ field: string; current: string; previous: string }> =
      []
    const resultJson = metricOutput.result_json
    if (
      resultJson?.regex_pattern &&
      resultJson.regex_pattern !== (this.metric_metadata?.regex_pattern || "")
    ) {
      diffs.push({
        field: "Regex Pattern",
        current: this.metric_metadata?.regex_pattern || "",
        previous: resultJson.regex_pattern as string,
      })
    }
    return diffs
  }

  getHighlightedMessages(mo: RunMetricOutput): number[] {
    if (mo.subvalues_by_timestamp?.length) {
      return mo.subvalues_by_timestamp
        .filter((sv) => sv.message_index != null)
        .map((sv) => sv.message_index as number)
    }
    const explanation = parseResultJson<TranscriptRegexMatchExplanation>(mo)
    if (!explanation?.matching_message_indices) {
      return []
    }
    return explanation.matching_message_indices
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const explanation =
        parseResultJson<TranscriptRegexMatchExplanation>(mo) ?? {}
      const highlightedMessages = this.getHighlightedMessages(mo)
      const matchFound = mo.subvalues_by_timestamp?.length
        ? highlightedMessages.length > 0
        : (explanation.match_found ?? highlightedMessages.length > 0)
      const pattern =
        explanation.regex_pattern ??
        this.metric_metadata?.regex_pattern ??
        undefined
      const role = explanation.role ?? this.metric_metadata?.role ?? undefined
      const matchMode =
        explanation.match_mode ?? this.metric_metadata?.match_mode
      const summary = buildMatchSummary({
        matchFound,
        totalMatches: highlightedMessages.length,
        pattern,
        role,
        matchMode,
      })

      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-sm text-amber-600 font-medium mb-1">
                Match Found
              </div>
              <div className="text-2xl font-bold text-amber-800">
                {matchFound ? "Yes" : "No"}
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-sm text-amber-600 font-medium mb-1">
                Total Matches
              </div>
              <div className="text-2xl font-bold text-amber-800">
                {highlightedMessages.length}
              </div>
            </div>
          </div>
          {summary && <div className="text-sm text-gray-700">{summary}</div>}
        </div>
      )
    } catch (error) {
      log.warn("Error parsing transcript regex match explanation", {
        error: error instanceof Error ? error.message : String(error),
      })
      return (
        <div className="text-red-500">
          Error displaying transcript regex match information
        </div>
      )
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <RegexConfig metricRecordData={metricRecordData} onChange={onChange} />
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    return (
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="font-semibold">Regex Pattern: </h3>
          <p>{metricRecordData.metric_metadata.regex_pattern}</p>
        </div>
        <div>
          <h3 className="font-semibold">Role: </h3>
          <p>{metricRecordData.metric_metadata.role}</p>
        </div>
        {metricRecordData.metric_metadata.match_mode && (
          <div>
            <h3 className="font-semibold">Match Mode: </h3>
            <p>
              {metricRecordData.metric_metadata.match_mode === "absence"
                ? "Absence"
                : "Presence"}
            </p>
          </div>
        )}
        {metricRecordData.metric_metadata.position && (
          <div>
            <h3 className="font-semibold">Position: </h3>
            <p>
              {metricRecordData.metric_metadata.position === "first"
                ? "First message"
                : metricRecordData.metric_metadata.position === "last"
                  ? "Last message"
                  : "Any message"}
            </p>
          </div>
        )}
        {metricRecordData.metric_metadata.case_insensitive !== undefined && (
          <div>
            <h3 className="font-semibold">Case Sensitivity: </h3>
            <p>
              {metricRecordData.metric_metadata.case_insensitive
                ? "Case Insensitive"
                : "Case Sensitive"}
            </p>
          </div>
        )}
        {metricRecordData.metric_metadata.use_knowledge_base !== undefined && (
          <div>
            <h3 className="font-semibold">Knowledge Base: </h3>
            <p>
              {metricRecordData.metric_metadata.use_knowledge_base
                ? "Enabled"
                : "Disabled"}
            </p>
          </div>
        )}
      </div>
    )
  }

  renderMetricType(): React.ReactNode {
    return "Transcript Regex Match"
  }
}
